import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaPaperPlane, FaGlobeAmericas, 
  FaChevronRight, FaCheckCircle, FaStar, FaChevronDown, 
  FaTimesCircle, FaClock, FaBus, FaHotel, FaUtensils, FaShieldAlt 
} from 'react-icons/fa';
import './MathuraVrindavanYatra.css';

const MathuraVrindavanYatra = () => {
  const [openSection, setOpenSection] = useState(null);

  const itineraryRef = useRef(null);
  const highlightsRef = useRef(null);
  const inclusionsRef = useRef(null);
  const aboutRef = useRef(null);

  const scrollToSection = (ref) => {
    window.scrollTo({ top: ref.current.offsetTop - 100, behavior: 'smooth' });
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const itineraryData = [
    {
      day: 1,
      title: "Arrival in Delhi & City Sightseeing",
      points: [
        "Arrival in Delhi, meet our team and transfer to the hotel.",
        "After freshening up, proceed for sightseeing in the capital city.",
        "Places to visit: Red Fort, India Gate, Lotus Temple.",
        "Visit Akshardham Temple and Qutub Minar.",
        "Overnight stay in Delhi."
      ],
      icon: <FaBus />
    },
    {
      day: 2,
      title: "Delhi to Agra",
      points: [
        "After breakfast, check out and travel towards Agra.",
        "Explore the world-famous Taj Mahal (Symbol of Love).",
        "Visit the historic Agra Fort.",
        "Evening free for local markets and shopping areas.",
        "Overnight stay in Agra."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 3,
      title: "Agra to Vrindavan (via Gokul)",
      points: [
        "Check out from Agra hotel and proceed towards Vrindavan.",
        "En-route visit to Gokul, associated with Lord Krishna's childhood.",
        "Visit Prem Mandir (famous for its lighting and architecture).",
        "Darshan at Shri Banke Bihari Temple and ISKCON Temple.",
        "Visit Chaar Dham Temple in Vrindavan.",
        "Overnight stay in Vrindavan."
      ],
      icon: <FaGlobeAmericas />
    },
    {
      day: 4,
      title: "Barsana & Govardhan Excursion",
      points: [
        "Proceed for a spiritual excursion to Barsana, the birthplace of Radha Rani.",
        "Visit Govardhan Hill, sacred site of the Govardhan Leela.",
        "Explore remaining local temples and surroundings of Vrindavan.",
        "Overnight stay in Vrindavan."
      ],
      icon: <FaMapMarkerAlt />
    },
    {
      day: 5,
      title: "Mathura Darshan & Departure",
      points: [
        "Check out and proceed towards Mathura (Birthplace of Lord Krishna).",
        "Visit Shri Krishna Janmabhoomi Temple complex.",
        "Experience the spiritual atmosphere at Vishram Ghat.",
        "Visit Shri Dwarkadhish Temple.",
        "Transfer to Mathura Railway Station for your onward journey.",
        "Tour concludes with divine blessings."
      ],
      icon: <FaCheckCircle />
    }
  ];

  return (
    <div className="trip-container">
      {/* 1. HEADER */}
      <motion.header initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="trip-header">
        <div className="header-left">
          <h1 className="trip-title">Mathura–Vrindavan & Barsana Divine Yatra – <br /> Delhi, Agra, Gokul, Vrindavan & Govardhan</h1>
          <p className="trip-duration">Duration: 4 Nights & 5 Days of Spiritual Bliss</p>
        </div>
        <div className="header-right">
          <div className="info-row"><FaMapMarkerAlt className="header-icon" /> <div className="info-text"><span>Destination:</span><p>Mathura, Vrindavan, Agra, Delhi</p></div></div>
          <div className="info-row"><FaPaperPlane className="header-icon" /> <div className="info-text"><span>Service Type:</span><p>Religious & Cultural Yatra</p></div></div>
          <div className="info-row type-tag"><FaGlobeAmericas className="header-icon" /><p>Divine Pilgrimage Tour</p></div>
        </div>
      </motion.header>

      {/* 2. IMAGE GRID (Aap yahan apni photos change kar sakte hain) */}
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="image-grid-container">
        <div className="grid-item large-vertical">
          <img src="https://s3.ap-south-1.amazonaws.com/awsimages.imagesbazaar.com/1200x1800-old/13878/DY500513.jpg?date=Thu%20Mar%2026%202026%2010:18:30%20GMT+0530%20(India%20Standard%20Time)" alt="Taj Mahal" />
        </div>
        <div className="grid-item middle-box">
          <img src="https://images.unsplash.com/photo-1662376391333-4a0a386782dd?q=80&w=389&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Prem Mandir" />
        </div>
        <div className="right-stack">
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1705927122615-02dcef3b1465?q=80&w=413&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="India Gate" />
          </div>
          <div className="grid-item small-h">
            <img src="https://images.unsplash.com/photo-1687626928969-f863d0e6ef33?q=80&w=1129&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" alt="Vrindavan Temple" />
          </div>
        </div>
      </motion.div>

      {/* 3. STICKY NAV */}
      <nav className="sticky-nav">
        <button className="tab-link" onClick={() => scrollToSection(itineraryRef)}>Detailed Itinerary</button>
        <button className="tab-link" onClick={() => scrollToSection(highlightsRef)}>Highlights</button>
        <button className="tab-link" onClick={() => scrollToSection(inclusionsRef)}>Policy & Inclusions</button>
        <button className="tab-link" onClick={() => scrollToSection(aboutRef)}>About Trip</button>
      </nav>

      <div className="content-section-wrapper">
        {/* ITINERARY SECTION */}
        <section ref={itineraryRef} className="scroll-section">
          <h2 className="section-main-title">Tour Itinerary</h2>
          <div className="days-list">
            {itineraryData.map((item) => (
              <motion.div key={item.day} initial="hidden" whileInView="visible" viewport={{ once: false, amount: 0.2 }} variants={fadeInUp} className="day-block">
                <div className="day-header">
                  <span className="day-badge">Day {item.day}</span>
                  <h3>{item.title}</h3>
                </div>
                <div className="day-body">
                  {item.points.map((p, i) => (
                    <div key={i} className="point-item">
                      <FaChevronRight className="bullet-icon" />
                      <p>{p}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* HIGHLIGHTS SECTION */}
        <motion.section ref={highlightsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Trip Highlights</h2>
          <div className="highlights-grid">
            <div className="h-item"><FaStar className="star-icon" /> <p>Shri Krishna Janmabhoomi (Mathura) - Birthplace of Krishna</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Divine Darshan at Banke Bihari Temple (Vrindavan)</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit Prem Mandir and ISKCON Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Explore Barsana (Birthplace of Radha Rani) & Govardhan Hill</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Visit the iconic Taj Mahal in Agra</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Spiritual moments at Vishram Ghat and Dwarkadhish Temple</p></div>
            <div className="h-item"><FaStar className="star-icon" /> <p>Delhi Sightseeing: Akshardham Temple and India Gate</p></div>
          </div>
        </motion.section>

        {/* INCLUSIONS & EXCLUSIONS */}
        <motion.section ref={inclusionsRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <div className="policy-box inclusion-box">
            <h4 className="p-title green"><FaCheckCircle /> What's Included</h4>
            <ul className="p-list">
              <li><FaHotel /> Accommodation in comfortable hotels (Delhi 1N, Agra 1N, Vrindavan 2N)</li>
              <li><FaClock /> 4 Nights stay on double sharing basis</li>
              <li><FaUtensils /> Meal Plan: 4 Breakfasts & 4 Dinners at hotels</li>
              <li><FaBus /> Transportation by AC Tempo Traveller (26 seater) for entire journey</li>
              <li><FaPaperPlane /> Pick-up from Delhi and Drop at Mathura Railway Station</li>
              <li><FaCheckCircle /> All Driver charges, fuel, toll taxes, parking, and permits</li>
              <li><FaShieldAlt /> Tour coordination and assistance throughout the journey</li>
            </ul>
          </div>
          <div className="policy-box exclusion-box">
            <h4 className="p-title red"><FaTimesCircle /> What's Excluded</h4>
            <ul className="p-list">
              <li><FaTimesCircle /> Airfare or train tickets for arrival and departure</li>
              <li><FaTimesCircle /> Entry fees for monuments, temples, or sightseeing parks</li>
              <li><FaTimesCircle /> Camera or video camera charges</li>
              <li><FaTimesCircle /> Personal expenses (Tips, Laundry, Shopping, etc.)</li>
              <li><FaUtensils /> Lunch or any meals not explicitly mentioned</li>
              <li><FaShieldAlt /> Travel insurance or any medical expenses</li>
              <li><FaTimesCircle /> Additional expenses due to weather or unforeseen circumstances</li>
            </ul>
          </div>
        </motion.section>

        {/* ABOUT SECTION */}
        <motion.section ref={aboutRef} initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section">
          <h2 className="section-main-title">Tour Overview</h2>
          <p className="about-text">
            The Mathura–Vrindavan & Barsana Divine Yatra is a spiritually enriching journey through the sacred land associated with Lord Krishna’s life and divine pastimes. This tour takes travelers through historic cities and holy towns including Delhi, Agra, Gokul, Mathura, Vrindavan, Barsana, and Govardhan.
            <br /><br />
            The journey combines spiritual temple visits with cultural sightseeing, including the iconic Taj Mahal in Agra. Devotees will experience the sacred atmosphere of Krishna’s birthplace, explore holy ghats, and visit sites of deep significance in Hindu mythology like Barsana and Govardhan.
          </p>
        </motion.section>

        {/* FOOTER LINKS (NEW PAGE REDIRECT) */}
        <div className="accordion-item">
          <Link to="/terms-conditions" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Terms & Conditions</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} /> 
          </Link>
        </div>

        <div className="accordion-item">
          <Link to="/privacy-policy" className="accordion-header" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span>Privacy Policy</span>
            <FaChevronDown className="arrow-icon" style={{ transform: 'rotate(-90deg)' }} />
          </Link>
        </div>

        {/* IMPORTANT NOTE */}
        <motion.section initial="hidden" whileInView="visible" viewport={{ once: false }} variants={fadeInUp} className="scroll-section travel-info-grid">
          <div className="info-card note-card" style={{ width: '100%' }}>
            <h4 className="info-card-title"><FaPaperPlane /> Important Note</h4>
            <p>Flight and train tickets can be arranged by <strong>Chandrama Holidays</strong> on request at the best available fares. Our team will assist you in planning convenient travel arrangements for a smooth journey.</p>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default MathuraVrindavanYatra;